// TenantContext - Sprint 33 (Multi-tenant).
//
// Provee:
//   - currentCompanyId: id de la empresa activa (del JWT + localStorage)
//   - companies: lista de empresas del usuario
//   - switchCompany(id): cambia empresa y emite evento tenant:changed
//   - refresh(): recarga lista
//
// El backend DERIVA la empresa SOLO del JWT (no del header).
// El header X-Company-Id que envia apiClient es defensa en profundidad.

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import apiClient from '../api/apiClient';

const TenantCtx = createContext(null);

const LS_KEY = 'stockpilot_company_id';

function readLS() {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v ? Number(v) : null;
  } catch { return null; }
}
function writeLS(v) {
  try {
    if (v) localStorage.setItem(LS_KEY, String(v));
    else localStorage.removeItem(LS_KEY);
  } catch { /* ignore */ }
}

function readFromJwt() {
  try {
    const u = apiClient.getUser && apiClient.getUser();
    return u && u.company_id ? Number(u.company_id) : null;
  } catch { return null; }
}

export function TenantProvider({ children }) {
  const [companies, setCompanies] = useState([]);
  const [currentCompanyId, setCurrent] = useState(() => readLS() || readFromJwt());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!apiClient.getAccessToken()) return;
    setLoading(true); setError(null);
    try {
      const r = await apiClient.get('/companies/mine');
      const items = (r && r.data && r.data.items) || [];
      setCompanies(items);
      const current = (r && r.data && r.data.current) || null;
      if (current && current.id && current.id !== currentCompanyId) {
        setCurrent(current.id);
        writeLS(current.id);
      }
    } catch (e) {
      setError((e && e.message) || 'Error');
    } finally {
      setLoading(false);
    }
  }, [currentCompanyId]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchCompany = useCallback((id) => {
    const n = Number(id);
    if (!n || n === currentCompanyId) return;
    setCurrent(n);
    writeLS(n);
    // Notificar a las vistas que deben invalidar cache y recargar
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tenant:changed', { detail: { companyId: n } }));
    }
  }, [currentCompanyId]);

  const logout = useCallback(() => {
    writeLS(null);
    setCompanies([]);
    setCurrent(null);
  }, []);

  const value = {
    currentCompanyId,
    companies,
    loading,
    error,
    switchCompany,
    refresh,
    logout,
  };

  return <TenantCtx.Provider value={value}>{children}</TenantCtx.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error('useTenant debe usarse dentro de <TenantProvider>');
  return ctx;
}

// Helper sin hook (para apiClient / fuera de React)
export function getCurrentCompanyId() {
  return readLS() || readFromJwt();
}
